/**
 *
 * @module httpClient
 * @author Greg Babula [greg.babula@disneystreaming.com]
 * @see https://github.github.io/fetch/
 * @see https://fetch.spec.whatwg.org/
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
 * @see https://github.com/bitinn/node-fetch/blob/master/LIMITS.md
 *
 */

import Check from 'check-types';
import Logging from './../../../logging/entry';
import Configuration from './../../configuration/entry';
import Exception from './../../exception/entry';
import CoreHttpClientProvider from './../shared/coreHttpClientProvider';

const HttpMethod = Configuration.HttpMethod;
const InvalidArgumentException = Exception.InvalidArgumentException;

const {
    like
} = Check.assert;

let fetch;

if (process.env.device_family === 'node') {
    fetch = require('node-fetch');
} else {
    fetch = require('./../../lib/whatwg-fetch');
}

const { Request, Headers } = fetch;

/**
 *
 * @class HttpClient
 * @extends {CoreHttpClientProvider}
 * @desc Isomorphic HttpClient implementation, powered by `whatwg-fetch` and `node-fetch`.
 *
 * @example
 * const httpClient = new HttpClient(logger);
 *
 */
export default class HttpClient extends CoreHttpClientProvider {

    /**
     *
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(logger = Logging) {

        super(logger);

        this.logger.log(HttpClient.name, 'Created Isomorphic Provider.');

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @note By default, the `credentials` option is `include`, this can be
     * overridden if necessary, however in general its expected to be `include` for
     * majority of POST requests.
     * @throws {NetworkException}
     * @returns {Promise<Object>}
     *
     * @example
     * httpClient.post(options).then((response) => { console.log(response.data); });
     *
     */
    post(options) {

        const errorMessage = {
            method: 'HttpClient.post(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        const { url, headers, body, bodyType, credentials = 'include' } = options;
        const { getResponseData, onError } = this;

        const request = new Request(url, {
            method: HttpMethod.POST,
            body,
            headers: new Headers(headers),
            credentials
        });

        return fetch(request).then((response) => {

            return getResponseData(response, bodyType);

        }).catch(onError);

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @note By default, the `credentials` option is `undefined`, this is by design as
     * the majority of GET requests do not need this option. There are certain edge-cases
     * that need this option.
     * @note When the `credentials` option is `undefined` and passed in to a newly
     * constructed `Request`, the `Request` `Object` will default to the correct value.
     * @throws {NetworkException}
     * @returns {Promise<Object>}
     *
     * @example
     * httpClient.get(options).then((response) => { console.log(response.data); });
     *
     */
    get(options) {

        const errorMessage = {
            method: 'HttpClient.get(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        const { url, headers, bodyType, credentials } = options;
        const { getResponseData, onError } = this;

        const request = new Request(url, {
            method: HttpMethod.GET,
            headers: new Headers(headers),
            mode: 'cors',
            credentials
        });

        return fetch(request).then((response) => {

            return getResponseData(response, bodyType);

        }).catch(onError);

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @note When the `credentials` option is `undefined` and passed in to a newly
     * constructed `Request`, the `Request` `Object` will default to the correct value.
     * @throws {NetworkException}
     * @returns {Promise<Object>}
     *
     * @example
     * httpClient.del(options).then((response) => { console.log(response.data); });
     *
     */
    del(options) {

        const errorMessage = {
            method: 'HttpClient.del(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        const { url, headers, body, bodyType, credentials } = options;
        const { getResponseData, onError } = this;

        const request = new Request(url, {
            method: HttpMethod.DELETE,
            body,
            headers: new Headers(headers),
            credentials
        });

        return fetch(request).then((response) => {

            return getResponseData(response, bodyType);

        }).catch(onError);

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @note When the `credentials` option is `undefined` and passed in to a newly
     * constructed `Request`, the `Request` `Object` will default to the correct value.
     * @throws {NetworkException}
     * @returns {Promise<Object>}
     *
     * @example
     * httpClient.patch(options).then((response) => { console.log(response.data); });
     *
     */
    patch(options) {

        const errorMessage = {
            method: 'HttpClient.patch(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        const { url, headers, body, bodyType, credentials } = options;
        const { getResponseData, onError } = this;

        const request = new Request(url, {
            method: HttpMethod.PATCH,
            body,
            headers: new Headers(headers),
            credentials
        });

        return fetch(request).then((response) => {

            return getResponseData(response, bodyType);

        }).catch(onError);

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @note When the `credentials` option is `undefined` and passed in to a newly
     * constructed `Request`, the `Request` `Object` will default to the correct value.
     * @throws {NetworkException}
     * @returns {Promise<Object>}
     *
     * @example
     * httpClient.put(options).then((response) => { console.log(response.data); });
     *
     */
    put(options) {

        const errorMessage = {
            method: 'HttpClient.put(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        const { url, headers, body, bodyType, credentials } = options;
        const { getResponseData, onError } = this;

        const request = new Request(url, {
            method: HttpMethod.PUT,
            body,
            headers: new Headers(headers),
            credentials
        });

        return fetch(request).then((response) => {

            return getResponseData(response, bodyType);

        }).catch(onError);

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {Object} [response={}] - The response Object.
     * @param {String} [bodyType=null] - The expected response data type, executed after initial JSON attempt
     * @desc Returns a consistent Object containing status, headers,
     * and data (parsed JSON output). If the response cannot be parsed, provide the
     * response as a plain String
     * @returns {Promise<Object>}
     * @note because the stream will be read multiple times, clone must be called
     * attempt to JSON.parse initially,
     *
     */
    getResponseData(response = {}, bodyType = null) {

        const { status, headers } = response;

        return response.clone().json().catch(() => {

            const hasBodyType = Check.assigned(bodyType);
            const hasArrayBufferType = Check.equal(bodyType, 'arrayBuffer');
            const hasArrayBufferSupport = Check.assigned(response.arrayBuffer);

            if (hasBodyType && hasArrayBufferType && hasArrayBufferSupport) {
                return response.arrayBuffer();
            }

            return response.text();

        }).then((data) => {

            return Promise.resolve({
                status,
                data,
                headers
            });

        });

    }

}



// WEBPACK FOOTER //
// ./src/services/platforms/isomorphic/httpClient.js